import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import PortalScene from './PortalScene';
import { SectorSignal } from './SectorEnemy';
import CoreboundTip from './CoreboundTip';
import { useCoreboundHelp } from './CoreboundHelp';
import PortalTransit from './PortalTransit';
import PortalUnlockSequence, { PORTAL_UNLOCK_MS } from './PortalUnlockSequence';
import { playSound } from '../../utils/utils';
import DoorIcon from './DoorIcon';
import RepairButtonAura from './RepairButtonAura';
import { BoardConditionBadge } from './BoardConditionNotice';
import { getRoomBoardCondition } from './boardConditions';
import { getDoorReward, getDoorType } from './doors';
import { getPortalChoices, HARD_PORTAL_COST } from './portalRoutes';
import { getPortalVisualKind } from './portalProfiles';
import { REPAIR_DESCRIPTION } from './economy';
import './PortalChamber.css';
import { usePopupTransition } from './popupTransition';
import { lockBodyScroll } from '../../utils/bodyScrollLock';

const PortalChamber = ({
  game,
  reduceMotion,
  onSelect,
  onRepair,
  repairCost,
  repairFeedback,
  marketContent,
  onUnlockSeen,
}) => {
  const { dismissTip } = useCoreboundHelp();
  const hasRelicStore =
    !!game.relicOffers?.length && game.marketPurchased !== game.floor;
  const [activeId, setActiveId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [enteringId, setEnteringId] = useState(null);
  const [purchasingId, setPurchasingId] = useState(null);
  const [celebrating, setCelebrating] = useState(
    () =>
      game.portalRoutes.hardUnlocked &&
      !game.portalRoutes.intro &&
      game.floor !== 5 &&
      !game.portalRoutes.unlockSeen
  );
  const purchaseTimer = useRef(null);
  const unlockSeenRef = useRef(onUnlockSeen);
  const [marketOpen, setMarketOpen] = useState(
    () => hasRelicStore && (game.floor === 2 || game.shards > 0)
  );
  unlockSeenRef.current = onUnlockSeen;
  useEffect(() => () => clearTimeout(purchaseTimer.current), []);
  useEffect(() => {
    if (!celebrating || marketOpen) return undefined;
    const timer = setTimeout(
      () => {
        setCelebrating(false);
        unlockSeenRef.current?.();
      },
      reduceMotion ? 2400 : 3200
    );
    return () => clearTimeout(timer);
  }, [celebrating, reduceMotion, marketOpen]);
  const enteringRef = useRef(false);
  const selectRef = useRef(onSelect);
  const marketTriggerRef = useRef(null);
  const marketDialogRef = useRef(null);
  const marketCloseRef = useRef(null);
  selectRef.current = onSelect;
  const choices = useMemo(() => getPortalChoices(game), [game]);
  const routes = game.portalRoutes;
  const selectedChoice =
    choices.find((choice) => choice.id === selectedId) ||
    choices.find((choice) => !choice.locked) ||
    choices[0] ||
    null;
  const scenes = useMemo(
    () =>
      choices.map((choice) => ({
        ...choice,
        color: choice.color || getDoorType(choice.kind)?.color,
        locked: choice.locked && purchasingId !== choice.id,
        newlyStable:
          purchasingId === choice.id ||
          (routes.outcome === 'strong' &&
            !routes.unlockSeen &&
            choice.routeClass === 'hard' &&
            !choice.locked),
      })),
    [choices, routes.outcome, routes.unlockSeen, purchasingId]
  );
  const routeNames = [
    'The Breach',
    ...game.history
      .slice()
      .reverse()
      .filter(({ floor }) => floor > 1)
      .map(({ roomName }) => roomName),
  ];
  const enterPortal = (id) => {
    const choice = choices.find((entry) => entry.id === id);
    if (
      enteringRef.current ||
      !choice ||
      (choice.locked && !choice.canPurchase)
    )
      return;
    playSound('/sounds/die-pre-entering-portal.mp3', { preventOverlap: true });
    dismissTip('portals');
    enteringRef.current = true;
    const enter = () => {
      if (reduceMotion) {
        playSound('/sounds/enter-portal-6.mp3', {
          preventOverlap: true,
        });
        selectRef.current(id);
      } else {
        setEnteringId(id);
      }
    };
    if (choice.entryCost) {
      setPurchasingId(id);
      playSound('/sounds/futuristic-zoom.mp3', { preventOverlap: true });
      purchaseTimer.current = setTimeout(
        enter,
        reduceMotion ? 900 : PORTAL_UNLOCK_MS
      );
    } else enter();
  };
  const previewPortal = (id) => {
    if (enteringRef.current || !choices.some((choice) => choice.id === id))
      return;
    setSelectedId(id);
    setActiveId(id);
  };
  const finishCloseMarket = useCallback(() => {
    setMarketOpen(false);
    requestAnimationFrame(() => {
      marketTriggerRef.current?.focus({ preventScroll: true });
    });
  }, []);
  const { close: closeMarket, popupClass: marketPopupClass } =
    usePopupTransition(finishCloseMarket, { open: marketOpen, reduceMotion });
  useEffect(() => {
    if (!marketOpen) return undefined;
    const unlock = lockBodyScroll();
    const focusFrame = requestAnimationFrame(() =>
      marketCloseRef.current?.focus({ preventScroll: true })
    );
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMarket();
        return;
      }
      if (event.key !== 'Tab') return;
      const controls = Array.from(
        marketDialogRef.current?.querySelectorAll(
          'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), [tabindex="0"]'
        ) || []
      ).filter((element) => element.tabIndex >= 0);
      if (!controls.length) return;
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      unlock();
      cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [marketOpen, closeMarket]);

  return (
    <section className="coreboundPanel coreboundPortalChamber">
      <header className="portalChamberHeader">
        <SectorSignal sector={game.floor} />
        <div className="portalChamberIntro">
          <div className="portalChamberHeading">
            <h2>Choose your next portal</h2>
          </div>
          {routes.intro ? (
            <p className="portalChamberGuidance">
              Your first throw collected a starting reward. Choose a portal to
              begin Sector {game.floor}. The Anomaly is free to enter here; win
              a later battle in one attack to open it for free again.
            </p>
          ) : routes.outcome !== 'strong' ? (
            <p className="portalChamberGuidance">
              {routes.outcome === 'recovery'
                ? 'You made it through. Two easier routes are open. Your landing rewards are still yours.'
                : game.floor === 5
                ? 'One guardian stands between you and Sector 6. Defeat the Warden to continue.'
                : `Two routes are free. Open and enter the hard portal for ${HARD_PORTAL_COST} shards, or earn free entry with a first-attack victory in the next sector.`}
            </p>
          ) : null}
        </div>
        {hasRelicStore ? (
          <div className="portalMarketActions">
            <button
              ref={marketTriggerRef}
              type="button"
              className="portalMarketShortcut"
              aria-label={`Open Signal Exchange. ${game.shards} shards available.`}
              aria-haspopup="dialog"
              aria-expanded={marketOpen}
              onClick={() => {
                setMarketOpen(true);
              }}
              disabled={!!enteringId || !!purchasingId}
            >
              <svg
                className="portalMarketLink"
                viewBox="0 0 32 32"
                aria-hidden="true"
              >
                <path
                  className="portalMarketLinkFrame"
                  d="M16 3 26 9V23L16 29 6 23V9Z"
                />
                <path d="M6 12H1M6 20H1M26 12H31M26 20H31M16 3V0M16 29V32M11 11 16 8 21 11V21L16 24 11 21Z" />
                <circle
                  className="portalMarketLinkCore"
                  cx="16"
                  cy="16"
                  r="3"
                />
                <circle
                  className="portalMarketLinkPacket"
                  cx="2"
                  cy="12"
                  r="1.5"
                />
                <circle
                  className="portalMarketLinkPacket"
                  cx="30"
                  cy="20"
                  r="1.5"
                />
              </svg>
              <span className="portalMarketShortcutLabel">
                Open Signal Exchange
              </span>
              <span className="portalMarketBalance">
                <strong>✦ {game.shards}</strong>
              </span>
            </button>
          </div>
        ) : (
          <div className="portalHealingShortcutGroup">
            <button
              ref={marketTriggerRef}
              type="button"
              className="coreboundRepair portalHealingShortcut"
              title={REPAIR_DESCRIPTION}
              onClick={() => {
                onRepair();
              }}
              disabled={
                !!enteringId || !!purchasingId || game.shards < repairCost
              }
            >
              <RepairButtonAura />
              <DoorIcon kind="Recovery" /> Heal 3 HP · {repairCost} shards
            </button>
            <p className="coreboundRepairHint">
              Each extra heal here costs 2 more shards.
            </p>
            {repairFeedback && (
              <p
                key={repairFeedback.key}
                className="portalHealingStatus"
                role="status"
              >
                Restored {repairFeedback.healed} HP.
                {repairFeedback.maxHpGained > 0 &&
                  ` Maximum grew by ${repairFeedback.maxHpGained}.`}
              </p>
            )}
          </div>
        )}
      </header>
      {!marketOpen && !enteringId && !purchasingId && !celebrating && (
        <CoreboundTip id="portals" title="Compare the next encounter">
          <p>
            Select a route button to preview it; tap the portal itself to enter.
            Compare enemy HP, damage when it hits back, board conditions, and
            victory loot before committing.
          </p>
          <p>
            Your final roll becomes attack damage. If the guardian survives, it
            hits back and you throw again; its health stays reduced. A
            first-attack victory earns free hard-portal entry. Sector 2 starts
            with all routes free; later hard routes otherwise cost{' '}
            {HARD_PORTAL_COST} shards.
          </p>
          <p>
            Spend shards on healing or market relics before choosing, or save
            them for later. Info is always available from the navigation.
          </p>
        </CoreboundTip>
      )}
      <div className="portalChamberStage">
        {celebrating && !marketOpen && !purchasingId && (
          <div
            className={
              'coreboundHardPortalUnlock' + (reduceMotion ? ' is-reduced' : '')
            }
            role="status"
            aria-live="polite"
          >
            <i aria-hidden="true">◇</i>
            <strong>
              {purchasingId ? 'HARD PORTAL OPENING' : 'HARD PORTAL UNLOCKED'}
            </strong>
            <span>
              {purchasingId
                ? '3 shards · Entering the harder route…'
                : 'Won in one attack · Harder route opened'}
            </span>
          </div>
        )}
        {purchasingId && !enteringId && (
          <PortalUnlockSequence
            shards={game.shards}
            reduceMotion={reduceMotion}
          />
        )}
        {!enteringId && (
          <PortalScene
            routes={scenes}
            reduceMotion={reduceMotion}
            activeId={activeId || selectedChoice?.id}
            showLabels
            onSelect={enterPortal}
          />
        )}
        <span className="portalChamberReticle" aria-hidden="true">
          +
        </span>
      </div>
      <div className="portalChoiceInterface">
        <div className="portalChoiceGrid">
          {choices.map((choice) => (
            <button
              key={choice.id}
              type="button"
              className={`portalChoice ${choice.locked ? 'is-locked' : ''}`}
              style={{
                '--portal-color':
                  choice.color || getDoorType(choice.kind)?.color,
              }}
              disabled={
                (choice.locked && !choice.canPurchase) ||
                !!enteringId ||
                !!purchasingId
              }
              onMouseEnter={() => setActiveId(choice.id)}
              onMouseLeave={() => setActiveId(null)}
              onFocus={() => setActiveId(choice.id)}
              onBlur={() => setActiveId(null)}
              onClick={() => enterPortal(choice.id)}
            >
              <span className="portalChoiceKind">
                <DoorIcon kind={choice.kind} />
                <strong>{getPortalVisualKind(choice)}</strong>
                <small>
                  {choice.routeClass === 'warden'
                    ? 'WARDEN GATE'
                    : choice.routeClass === 'hard'
                    ? 'HARD'
                    : choice.routeClass === 'safe'
                    ? 'LOWER RISK'
                    : 'STANDARD'}
                </small>
              </span>
              <span className="portalChoiceStats">
                Enemy {choice.target} HP · Hits back for {choice.damage} HP
              </span>
              <span className="portalChoiceReward">
                Loot: {getDoorReward(choice)}
              </span>
              <BoardConditionBadge
                condition={getRoomBoardCondition(game, choice.id)}
              />
              <span className="portalChoiceAction">
                {choice.locked
                  ? routes.intro
                    ? 'HARD ROUTE · AVAILABLE AFTER THIS SECTOR'
                    : choice.canPurchase
                    ? 'Unlock & enter · 3 shards →'
                    : 'Need 3 shards to unlock'
                  : choice.routeClass === 'hard'
                  ? 'Enter hard portal →'
                  : 'Enter portal →'}
              </span>
            </button>
          ))}
        </div>
        <div className="portalMobilePicker">
          <div
            className="portalMobileTabs"
            role="group"
            aria-label="Portal routes"
          >
            {choices.map((choice) => {
              const selected = choice.id === selectedChoice?.id;
              const routeLabel = choice.locked
                ? choice.entryCost
                  ? '3 SHARDS'
                  : 'LOCKED'
                : choice.routeClass === 'warden'
                ? 'WARDEN GATE'
                : choice.routeClass === 'hard'
                ? 'HARD'
                : choice.routeClass === 'safe'
                ? 'SAFER'
                : 'STANDARD';
              const tabLabel = `${choice.kind}, ${routeLabel}${
                choice.locked
                  ? routes.intro
                    ? ', available after this sector'
                    : ', costs 3 shards to unlock and enter'
                  : ''
              }`;
              return (
                <button
                  key={choice.id}
                  type="button"
                  className={`portalMobileTab ${
                    selected ? 'is-selected' : ''
                  } ${choice.locked ? 'is-locked' : ''}`}
                  style={{ '--portal-color': getDoorType(choice.kind)?.color }}
                  aria-label={tabLabel}
                  aria-pressed={selected}
                  onClick={() => previewPortal(choice.id)}
                >
                  <span className="portalMobileTabKind">
                    <DoorIcon kind={choice.kind} />
                    {choice.kind}
                  </span>
                  <small>{routeLabel}</small>
                </button>
              );
            })}
          </div>
          {selectedChoice && (
            <section
              id="portalSelectedDetails"
              className={`portalMobileDetails ${
                selectedChoice.locked ? 'is-locked' : ''
              }`}
              style={{
                '--portal-color': getDoorType(selectedChoice.kind)?.color,
              }}
              aria-live="polite"
              aria-label={`${selectedChoice.kind} portal details`}
            >
              <div className="portalMobileDetailHeading">
                <span className="portalMobileKind">
                  <DoorIcon kind={selectedChoice.kind} />
                  <strong>{selectedChoice.kind}</strong>
                </span>
                <span
                  className={`portalMobileRisk portalMobileRisk-${selectedChoice.routeClass}`}
                >
                  {selectedChoice.locked
                    ? selectedChoice.canPurchase
                      ? '3 SHARDS'
                      : 'LOCKED'
                    : selectedChoice.routeClass === 'warden'
                    ? 'WARDEN GATE'
                    : selectedChoice.routeClass === 'hard'
                    ? 'HIGH RISK'
                    : selectedChoice.routeClass === 'safe'
                    ? 'LOWER RISK'
                    : 'STANDARD'}
                </span>
              </div>
              <div className="portalMobileStats">
                <span>
                  Minion <strong>{selectedChoice.target} HP</strong>
                </span>
                <span>
                  Hits back for <strong>{selectedChoice.damage} HP</strong>
                </span>
                <span>
                  Loot <strong>{getDoorReward(selectedChoice)}</strong>
                </span>
              </div>
              <div className="portalMobileDetailFooter">
                <BoardConditionBadge
                  condition={getRoomBoardCondition(game, selectedChoice.id)}
                />
                {selectedChoice.locked && !selectedChoice.canPurchase ? (
                  <span className="portalMobileLockedHint">
                    {routes.intro
                      ? 'Available after this sector'
                      : `Need 3 shards · You have ${game.shards}`}
                  </span>
                ) : (
                  <button
                    type="button"
                    className="portalMobileEnter"
                    disabled={!!enteringId || !!purchasingId}
                    onClick={() => enterPortal(selectedChoice.id)}
                  >
                    {selectedChoice.entryCost
                      ? 'Unlock & enter · 3 shards'
                      : selectedChoice.routeClass === 'hard'
                      ? 'Enter hard portal'
                      : 'Enter portal'}{' '}
                    <span aria-hidden="true">→</span>
                  </button>
                )}
              </div>
            </section>
          )}
        </div>
      </div>
      {enteringId && (
        <PortalTransit
          route={scenes.find(({ id }) => id === enteringId)}
          dieId={game.selectedDie}
          floor={game.floor}
          onComplete={() => selectRef.current(enteringId)}
        />
      )}
      <ol className="portalRouteTrail" aria-label="Your route so far">
        {routeNames.map((name, index) => (
          <li key={`${index}-${name}`}>
            <span>{String(index + 1).padStart(2, '0')}</span>
            {name}
          </li>
        ))}
        <li aria-current="step">
          <span>{String(game.floor).padStart(2, '0')}</span>Choose a portal
        </li>
        <li className="portalTrailCore">
          <span>10</span>The Core
        </li>
      </ol>
      {marketOpen &&
        marketContent &&
        createPortal(
          <div
            className="portalMarketDialogBackdrop"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) closeMarket();
            }}
          >
            <motion.section
              ref={marketDialogRef}
              className={`portalMarketDialog ${marketPopupClass}`}
              layout={!reduceMotion}
              transition={{
                layout: { duration: reduceMotion ? 0 : 0.28, ease: 'easeOut' },
              }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="coreboundRunMarketTitle"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <div className="portalMarketDialogHeader">
                <span>SECTOR {game.floor} · SIGNAL EXCHANGE</span>
                <span className="portalMarketWallet" role="status">
                  Available shards: <strong>{game.shards}</strong>
                </span>
                <button
                  ref={marketCloseRef}
                  type="button"
                  aria-label="Close Signal Exchange"
                  onClick={closeMarket}
                >
                  Close ×
                </button>
              </div>
              {marketContent}
            </motion.section>
          </div>,
          document.body
        )}
    </section>
  );
};

export default PortalChamber;
